import React, { useContext, useState } from 'react';
import { Star, Pencil, Trash2 } from 'lucide-react';
import sessionsService from '../../../services/sessionsServices';
import { TemporaryChatContext } from '../../../Context/temporaryChatContext';
import { useLocation, useNavigate } from 'react-router-dom';
import Loaders from '../../Loaders';
import RenameChatModal from '../RenameChatModal';
import DeleteChatModal from '../DeleteChatModal';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Toast from '../../Toast';
import { useSidebarSessions, sessionsInvalidateKey } from '../../../hooks';
import { CardMenu } from '../../ui';
import { cn } from '../../../lib/utils';

const Timeline = ['Today', 'Yesterday', 'Last 7 Days', 'Last 30 Days'];

const SessionsListSection = ({ selectedAgent }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();
  const { toggleRef } = useContext(TemporaryChatContext);

  const payload = {
    platform_id: 'coinpilotlabs',
    agent_id: selectedAgent?.value,
    project_id: 'test',
  };

  const { data, isPending } = useSidebarSessions(payload);
  const [deleteChat, setDeleteChat] = useState(null);
  const [renameChat, setRenameChat] = useState(null);

  const handleAddFavorite = useMutation({
    mutationFn: async id =>
      sessionsService?.updateSession({
        platform_id: 'coinpilotlabs',
        session_id: id,
        is_favorite: true,
      }),
    retry: 1,
    onSuccess: response => {
      Toast({ type: 'success', message: response.detail });
      queryClient.invalidateQueries({ queryKey: sessionsInvalidateKey });
    },
    onError: err => {
      Toast({ type: 'error', message: err.detail });
    },
  });

  const handleRemoveFavorite = useMutation({
    mutationFn: async id =>
      sessionsService?.updateSession({
        platform_id: 'coinpilotlabs',
        session_id: id,
        is_favorite: false,
      }),
    retry: 1,
    onSuccess: response => {
      Toast({ type: 'success', message: response.detail });
      queryClient.invalidateQueries({ queryKey: sessionsInvalidateKey });
    },
    onError: err => {
      Toast({ type: 'error', message: err.detail });
    },
  });

  const openChat = chat => {
    toggleRef(true);
    navigate(`/chat/${chat?.id}`);
  };

  const renderChatItem = (chat, { favorite } = {}) => {
    const active = location.pathname?.includes(chat?.id);
    return (
      <li key={chat.id}>
        <div
          className={cn(
            'group flex items-center gap-1 rounded-lg transition-colors',
            active ? 'bg-black text-primary' : 'bg-white/10 text-white hover:bg-black hover:text-primary',
          )}>
          <button
            type="button"
            onClick={() => openChat(chat)}
            className="flex-1 min-w-0 text-left px-2.5 py-2.5 text-[13px] font-medium truncate"
            dangerouslySetInnerHTML={{ __html: chat?.title }}
          />
          <div className="pr-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <CardMenu
              items={[
                favorite
                  ? {
                      label: 'Remove from Favorites',
                      icon: Star,
                      onClick: () => handleRemoveFavorite.mutate(chat?.id),
                    }
                  : {
                      label: 'Add to Favorites',
                      icon: Star,
                      onClick: () => handleAddFavorite.mutate(chat?.id),
                    },
                {
                  label: 'Rename',
                  icon: Pencil,
                  onClick: () => setRenameChat(chat),
                },
                {
                  label: 'Delete',
                  icon: Trash2,
                  onClick: () => setDeleteChat(chat),
                  danger: true,
                },
              ]}
            />
          </div>
        </div>
      </li>
    );
  };

  return (
    <div>
      {isPending ? (
        <Loaders textLoader />
      ) : (
        <>
          {data?.sessions?.Favorite?.length > 0 && (
            <div className="mb-5">
              <span className="block text-xs text-[#afafaf] font-medium mb-2">Favorites</span>
              <ul className="space-y-2">
                {data.sessions.Favorite.map(chat => renderChatItem(chat, { favorite: true }))}
              </ul>
            </div>
          )}

          {Timeline.map(time =>
            data?.sessions?.[time]?.length > 0 ? (
              <div key={time} className="mb-5">
                <span className="block text-xs text-[#afafaf] font-medium mb-2">{time}</span>
                <ul className="space-y-2">{data.sessions[time].map(chat => renderChatItem(chat))}</ul>
              </div>
            ) : null,
          )}
        </>
      )}

      {renameChat && <RenameChatModal onClose={() => setRenameChat(null)} data={renameChat} />}

      {deleteChat && <DeleteChatModal onClose={() => setDeleteChat(null)} data={deleteChat} />}
    </div>
  );
};

export default SessionsListSection;
